"use client";
import React, { useEffect, useRef, useState } from "react";
import { IHiddenPhones } from "@/types/hiddenPhones.types";
import EditIcon from "@/icons/contacts/edit.svg";
import DeleteIcon from "@/icons/contacts/remove.svg";
import PhoneIcon from "@/icons/contacts/phone.svg";
import ChevronDownIcon from "@/icons/contacts/chevronDown.svg";
import ChevronRightIcon from "@/icons/contacts/chevronRight.svg";
import cn from "clsx";
import {
  getDeveloperRoleBgClass,
} from "@/utils/getDeveloperRoleColor";
import { useTranslations } from "next-intl";

interface Props {
  data: IHiddenPhones;
  onEdit?: () => void;
  onDelete?: () => void;
  isLinked?: boolean;
  isOwner?: boolean;
}

const HiddenContactCard: React.FC<Props> = (props) => {
  const filteredPhones = props.data.phones.filter((i) => !i.delete);
  const [sliceIndex, setSliceIndex] = useState<number>(
    filteredPhones.length < 4 ? filteredPhones.length : 2,
  );
  const ref = useRef<HTMLSpanElement>(null);

  const [expanded, setExpanded] = useState<boolean>(false);
  const [isClamped, setIsClamped] = useState<boolean>(false);
  const t = useTranslations("HiddenFields");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // если реальная высота больше — значит обрезан
    setIsClamped(el.scrollHeight > el.clientHeight);
  }, [props.data.description]);

  return (
    <div className="shadow-[5px_5px_25px_15px_#EBEBEB40] border-1 border-[##ECECEC] rounded-[20px] p-[27px] flex flex-col gap-4 items-start min-h-368px h-full">
      <div className="flex justify-between w-full items-start">
        <h4 className="text-[#1B1B1B] font-medium text-[21px] leading-[100%] tracking-[0%] align-middle whitespace-normal break-words hyphens-auto max-w-[80%]">
          {props.data.contactName}
        </h4>
        <div className="flex items-center gap-[19px]">
          {props.onEdit && (props.data.canEdit || props.data.customId || props.isOwner) && (
            <button onClick={props.onEdit}>
              <EditIcon />
            </button>
          )}
          {props.onDelete && (props.data.canEdit || props.data.customId || props.isOwner) && (
            <button onClick={props.onDelete}>
              <DeleteIcon />
            </button>
          )}
        </div>
      </div>
      {!props.isOwner && props.data.role && (
        <div
          className={cn(
            "px-[20px] py-[8px] rounded-[10px]",
            getDeveloperRoleBgClass(props.data.role),
          )}
        >
          <span className="text-white font-medium text-[16px] leading-[100%] tracking-[0%] align-middle">
            {t(props.data.role)}
          </span>
        </div>
      )}
      <div className="flex items-start gap-4">
        <div>
          <PhoneIcon />
        </div>
        <div className="flex flex-col items-start gap-3">
          {filteredPhones.slice(0, sliceIndex).map((contact, index) => (
            <div
              key={contact.id || contact.customId}
              className="flex items-center gap-[15px] flex-wrap"
            >
              {props.isLinked ? (
                <a
                  href={`tel:${contact.phone}`}
                  className="text-[#4D4D4D] font-normal text-[18px] leading-[100%] align-middle"
                >
                  {contact.phone}
                </a>
              ) : (
                <span className="text-[#4D4D4D] font-normal text-[18px] leading-[100%] align-middle">
                  {contact.phone}
                </span>
              )}
              <div className="px-[14px] py-[7px] rounded-[10px] bg-[#EEEEEE]">
                <span className="text-[#5B5B5B] text-[15px] font-normal leading-[100%] tracking-[0] align-middle">
                  {t(contact.type)}
                </span>
              </div>
            </div>
          ))}
          {sliceIndex < filteredPhones.length && (
            <button
              onClick={() => setSliceIndex(filteredPhones.length)}
              className="flex items-center gap-3"
            >
              <span className="text-[#323232] font-medium text-[18px] text-wrap">
                {t("seeMore")}{" "}
                <span className="text-[#CF1409]">
                  {filteredPhones.length - sliceIndex}
                </span>
              </span>
              <ChevronDownIcon />
            </button>
          )}
        </div>
      </div>
      <div className="w-full flex-1">
        <span
          ref={ref}
          className={cn(
            "text-[#1B1B1B] font-normal text-[15px] leading-[22px] text-wrap",
            "whitespace-normal",
            "break-words",
            "hyphens-auto",
            !expanded && "line-clamp-3",
          )}
        >
          {props.data.description}
        </span>
      </div>

      {isClamped && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="  flex items-center gap-3"
        >
          <span className="text-[#323232] font-medium text-[18px]">
            {t("moreDetails")}
          </span>
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
};

export default HiddenContactCard;
